'use client';

import { useEffect, useCallback, useRef } from 'react';
import { useWebSocketStore } from '../store/websocket-store';
import type {
  WebSocketConnection as WSConnectionType,
  WebSocketMessage as WSMessageType,
} from '../electron/proxy-types';

// Singleton flag to prevent duplicate listeners
let wsListenerRegistered = false;

type WebSocketEventData =
  | { type: 'ws-connection'; data: WSConnectionType }
  | { type: 'ws-message'; data: WSMessageType }
  | {
      type: 'ws-close';
      data: { connectionId: string; code?: number; reason?: string };
    };

export function useWebSocketListener() {
  const { addConnection, updateConnection, addMessage, captureEnabled } =
    useWebSocketStore();

  // Use refs to get latest state in event handlers
  const addConnectionRef = useRef(addConnection);
  const updateConnectionRef = useRef(updateConnection);
  const addMessageRef = useRef(addMessage);
  const captureEnabledRef = useRef(captureEnabled);

  useEffect(() => {
    addConnectionRef.current = addConnection;
    updateConnectionRef.current = updateConnection;
    addMessageRef.current = addMessage;
    captureEnabledRef.current = captureEnabled;
  }, [addConnection, updateConnection, addMessage, captureEnabled]);

  const handleWebSocketEvent = useCallback((data: WebSocketEventData) => {
    if (!captureEnabledRef.current) return;

    switch (data.type) {
      case 'ws-connection': {
        const connection = data.data;

        addConnectionRef.current({
          id: connection.id,
          sequentialId: connection.sequentialId,
          url: connection.url,
          host: connection.host,
          timestamp: new Date(connection.timestamp),
          status: connection.status,
          closeCode: connection.closeCode,
          closeReason: connection.closeReason,
          protocol: connection.protocol,
          extensions: connection.extensions,
          messageCount: connection.messageCount,
          upgradeHeaders: connection.upgradeHeaders,
          source: 'proxy',
        });
        break;
      }

      case 'ws-message': {
        const message = data.data;

        // addMessage expects { ...message, data: string } where data is the full message content
        addMessageRef.current({
          id: message.id,
          connectionId: message.connectionId,
          sequentialId: message.sequentialId,
          direction: message.direction,
          timestamp: new Date(message.timestamp),
          dataType: message.dataType,
          dataPreview: '', // Will be computed from data
          data: message.data, // Full data - store will create preview
          dataSize: message.dataSize,
          opcode: message.opcode,
          isControlFrame: message.isControlFrame,
        });
        break;
      }

      case 'ws-close': {
        const { connectionId, code, reason } = data.data;

        updateConnectionRef.current(connectionId, {
          status: 'closed',
          closeCode: code,
          closeReason: reason,
        });
        break;
      }

      default:
        console.warn(
          'Unknown WebSocket event type:',
          (data as { type: string }).type,
        );
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.electronAPI) {
      return;
    }

    // Singleton pattern: Only register listener once
    if (wsListenerRegistered) {
      return;
    }

    const handleEvent = (...args: unknown[]) => {
      const data = args[0] as WebSocketEventData;

      handleWebSocketEvent(data);
    };

    // Register listeners for all WebSocket event types
    window.electronAPI.on('ws-connection', handleEvent);
    window.electronAPI.on('ws-message', handleEvent);
    window.electronAPI.on('ws-close', handleEvent);
    wsListenerRegistered = true;

    return () => {
      if (
        typeof window !== 'undefined' &&
        window.electronAPI &&
        window.electronAPI.removeListener
      ) {
        window.electronAPI.removeListener('ws-connection', handleEvent);
        window.electronAPI.removeListener('ws-message', handleEvent);
        window.electronAPI.removeListener('ws-close', handleEvent);
        wsListenerRegistered = false;
      }
    };
  }, [handleWebSocketEvent]);
}
